import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grow } from '@mui/material';
import { useState, useRef } from 'react';
import { AlertTypes } from '../../utilities/Enums/AlertTypes';
import CancelIcon from '@mui/icons-material/Cancel';
import { CheckCircleOutline, WarningRounded } from '@mui/icons-material';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { React } from 'Imports';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { isEncompass } from '$Utilities/integrationPlatform';

interface SnackbarOptions {
    key?: string | number; //use it if multiple snackbars can be shown
    message: React.ReactNode;
    autoHideDuration?: number | null;
    onSuccessLinkActionClick?: () => void;
    successLinkActionText?: string;
    onClose?: () => void;
    position?: 'top' | 'bottom';
    showIcon?: boolean;
    alertType: AlertTypes;
    useNewIcons?: boolean;
    isEncompass?: boolean;
    closeOnOutsideClick?: boolean; // if true, clicking outside the snackbar, in an actionable area (eg. button, link, dropdown, etc), will close it
}

export const useAppSnackbar = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const openSnackbars = useRef<Set<string | number>>(new Set());
    const outsideClickListeners = useRef<Map<string | number, (e: MouseEvent) => void>>(new Map());
    const [requestUndo, setRequestUndo] = useState(false);

    // This function removes the click listener for outside clicks when the snackbar is closed, to prevent memory leaks and unintended behavior
    const removeOutsideClickListener = (key: string | number | undefined) => {
        const k = key ?? '';
        const listener = outsideClickListeners.current.get(k);
        if (listener) {
            document.removeEventListener('click', listener);
            outsideClickListeners.current.delete(k);
        }
    };

    // This function checks if the click target is an interactive element (like a button, link, input, etc.) or has interactive roles/attributes, which would indicate that the snackbar should be closed if clicked outside
    const isInteractiveTarget = (target: EventTarget | null): boolean => {
        if (!target || !(target instanceof Element)) return false;
        const interactiveTags = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'];
        const interactiveRoles = ['button', 'link', 'option', 'menuitem', 'tab', 'checkbox', 'radio', 'combobox', 'listbox'];
        let el: Element | null = target as Element;
        while (el && el !== document.documentElement) {
            if (interactiveTags.indexOf(el.tagName) !== -1) return true;
            const role = el.getAttribute('role');
            if (role && interactiveRoles.indexOf(role) !== -1) return true;
            if (el.hasAttribute('aria-haspopup')) return true;
            const tabIndex = el.getAttribute('tabindex');
            if (tabIndex !== null && parseInt(tabIndex, 10) >= 0) return true;
            if (window.getComputedStyle(el).cursor === 'pointer') return true;
            el = el.parentElement;
        }
        return false;
    };

    const handleCloseSnackbar = (key?: string | number): void => {
        if (key) {
            closeSnackbar(key);
            openSnackbars.current.delete(key); // Ensure key is removed
        } else {
            closeSnackbar();
        }
        setRequestUndo(false);
    };

    const showSnackbarMessage = (props: SnackbarOptions) => {
        const { key, message, autoHideDuration = 5000, showIcon, alertType, useNewIcons } = props;

        const submit = (e: any) => {
            e.stopPropagation();
            setRequestUndo(true);
            props.onSuccessLinkActionClick && props.onSuccessLinkActionClick();
        };

        const defineIcon = (): JSX.Element => {
            if (showIcon && isEncompass)
                switch (alertType) {
                    case AlertTypes.Alert:
                        if (useNewIcons) {
                            return <FontAwesomeIcon icon={faTriangleExclamation} />;
                        }
                        return <CancelIcon>add_circle</CancelIcon>;
                    case AlertTypes.Success:
                        if (useNewIcons) {
                            return <FontAwesomeIcon icon={faCircleCheck} />;
                        }
                        return <CheckCircleOutline>add_circle</CheckCircleOutline>;
                    case AlertTypes.Fail:
                        return <WarningRounded>add_circle</WarningRounded>;
                }
            return <></>;
        };

        const getMessage = (): JSX.Element => {
            if (showIcon) {
                return (
                    <>
                        {defineIcon()}
                        <span style={{ paddingLeft: '16px' }}>{message}</span>
                    </>
                );
            }
            return (
                <>
                    <span>{message}</span>
                </>
            );
        };

        // Prevent duplicate snackbar
        if (openSnackbars.current.has(key ?? '')) {
            closeSnackbar(key); // Force close
            openSnackbars.current.delete(key ?? '');
        }

        openSnackbars.current.add(key ?? '');

        //set timeout to allow to fully close previous snackbar before showing a new one with the same key
        setTimeout(() => {
            enqueueSnackbar(getMessage(), {
                key,
                variant: AlertTypes.Success ? 'success' : alertType === AlertTypes.Fail ? 'error' : 'warning',
                autoHideDuration,
                hideIconVariant: true,
                anchorOrigin: { vertical: props.position ?? (isEncompass ? 'top' : 'bottom'), horizontal: 'center' },
                onClose: () => {
                    removeOutsideClickListener(key);
                    openSnackbars.current.delete(key ?? '');
                    props.onClose && props.onClose();
                },
                action: () => (
                    <>
                        {alertType == AlertTypes.Success && props.successLinkActionText && (
                            <Box
                                component={'span'}
                                onClick={submit}
                                sx={{
                                    textTransform: 'capitalize',
                                    textDecoration: 'underline',
                                    fontSize: '16px',
                                    paddingRight: '32px',
                                    paddingLeft: '16px',
                                    cursor: 'pointer',
                                    ...(requestUndo && {
                                        pointerEvents: 'none',
                                        opacity: 0.5,
                                        cursor: 'not-allowed',
                                    }),
                                }}
                            >
                                {props.successLinkActionText}
                            </Box>
                        )}
                        <Box sx={{ cursor: 'pointer', fontSize: '16px' }}>
                            <FontAwesomeIcon
                                color="inherit"
                                icon={faTimes}
                                onClick={() => {
                                    handleCloseSnackbar(key);
                                }}
                            />
                        </Box>
                    </>
                ),
                style: {
                    backgroundColor:
                        alertType === AlertTypes.Success
                            ? isEncompass
                                ? '#cceee4'
                                : '#e5fde9'
                            : alertType === AlertTypes.Alert
                              ? '#FEF9C3'
                              : '#FEE2E2',
                    color:
                        alertType === AlertTypes.Success
                            ? isEncompass
                                ? '#00573e'
                                : '#055724'
                            : alertType === AlertTypes.Alert
                              ? isEncompass
                                  ? '#B67038'
                                  : '#b67038'
                              : '#9F0D0D',
                    fontWeight: 500,
                    fontSize: '16px',
                    letterSpacing: 'normal',
                    paddingRight: '25px',
                    paddingLeft: '16px',
                    boxShadow: 'none',
                    marginTop: isEncompass && !props.position ? '80px' : 'auto', // 20px are applied by default. This matches a total of 100px needed for encompass.
                },
                TransitionComponent: Grow,
                onEntered: props.closeOnOutsideClick
                    ? () => {
                          // add click listener to the document to detect clicks outside the snackbar in an actionable area to close the snackbar
                          const listener = (e: MouseEvent) => {
                              const container = document.querySelector('.notistack-SnackbarContainer');
                              if (container && !container.contains(e.target as Node) && isInteractiveTarget(e.target)) {
                                  closeSnackbar(key);
                                  removeOutsideClickListener(key);
                              }
                          };
                          outsideClickListeners.current.set(key ?? '', listener);
                          document.addEventListener('click', listener);
                      }
                    : undefined,
            });
        }, 700);
    };

    return { showSnackbarMessage, handleCloseSnackbar };
};
